import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';
import { Calendar, Phone, Video, CheckCircle } from 'lucide-react';
import TimeSlotPicker from '../components/TimeSlotPicker';
import { trainingScheduleService, type CreateScheduleData } from '../services/training-schedule.service';

export default function OnboardingStep4() {
    const [isLoading, setIsLoading] = useState(false);
    const [existingSchedule, setExistingSchedule] = useState<any>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();

    const [formData, setFormData] = useState<CreateScheduleData>({
        preferredSlot1: null as any,
        preferredSlot2: undefined,
        preferredSlot3: undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        meetingType: 'phone',
        phoneNumber: '',
        notes: '',
    });

    // Load existing schedule if any
    useEffect(() => {
        loadSchedule();
    }, []);

    const loadSchedule = async () => {
        try {
            const response = await trainingScheduleService.getMySchedule();
            if (response.success && response.data) {
                setExistingSchedule(response.data);
                // If schedule exists and is confirmed/completed, continue to next step
                if (response.data.status === 'CONFIRMED' || response.data.status === 'COMPLETED') {
                    setSuccess(true);
                    setTimeout(() => {
                        navigate('/onboarding/step-5');
                    }, 2000);
                }
            }
        } catch (err: any) {
            console.error('Error loading schedule:', err);
        }
    };

    const validatePhoneNumber = (value: string): string => {
        if (!value.trim()) return 'Phone number is required for phone meetings';
        // Strip common formatting chars
        const stripped = value.replace(/[\s\-().]/g, '');
        // Remove optional leading +1 or 1 country code
        const digitsOnly = stripped.replace(/^\+?1/, '');
        if (!/^\d+$/.test(digitsOnly)) return 'Only digits, spaces, dashes, and parentheses are allowed';
        if (digitsOnly.length !== 10) return 'Please enter a valid 10-digit US phone number';
        return '';
    };

    const formatPhoneNumber = (raw: string): string => {
        // Keep only digits, strip country code prefix
        const digits = raw.replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
        if (digits.length === 0) return '';
        if (digits.length <= 3) return `(${digits}`;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhoneNumber(e.target.value);
        setFormData({ ...formData, phoneNumber: formatted });
        setPhoneError(formatted ? validatePhoneNumber(formatted) : '');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!formData.preferredSlot1) {
            setError('Please select at least one preferred time slot');
            return;
        }

        if (formData.meetingType === 'phone') {
            const phoneValidationError = validatePhoneNumber(formData.phoneNumber || '');
            if (phoneValidationError) {
                setPhoneError(phoneValidationError);
                return;
            }
        }

        setIsLoading(true);

        try {
            const response = await trainingScheduleService.createSchedule(formData);

            if (response.success) {
                setSuccess(true);

                // Refresh user data to get updated onboarding step
                await refreshUser();

                // Show success message and redirect to next onboarding step
                setTimeout(() => {
                    navigate('/onboarding/step-5');
                }, 2000);
            }
        } catch (err: any) {
            console.error('Error submitting schedule:', err);
            const errorMsg = err.response?.data?.error || err.message || 'Failed to submit training schedule';
            setError(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkip = () => {
        // Skip training and move to next onboarding step
        navigate('/onboarding/step-5');
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-10 h-10 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Training Schedule Submitted!
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Thank you for submitting your availability. Our team will review your preferred times and confirm your training session shortly.
                    </p>
                    <p className="text-sm text-gray-500">
                        Redirecting to dashboard...
                    </p>
                </div>
            </div>
        );
    }

    if (existingSchedule) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <div className="text-center mb-8">
                            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                Training Already Scheduled
                            </h1>
                            <p className="text-gray-600">
                                You have already submitted your training availability.
                            </p>
                        </div>

                        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                            <h3 className="font-medium text-gray-900 mb-4">Your Schedule Details</h3>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                        existingSchedule.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-800' :
                                        existingSchedule.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                                        'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {existingSchedule.status}
                                    </span>
                                </div>
                                {existingSchedule.confirmedSlot && (
                                    <div>
                                        <span className="font-medium text-gray-700">Confirmed Time:</span>
                                        <span className="ml-2 text-gray-900">
                                            {new Date(existingSchedule.confirmedSlot).toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                {existingSchedule.zoomJoinUrl && (
                                    <div>
                                        <a
                                            href={existingSchedule.zoomJoinUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-emerald-600 hover:text-emerald-700 underline"
                                        >
                                            Join Zoom Meeting
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 text-center">
                            <button
                                onClick={async () => {
                                    console.log('Continue to next step clicked');
                                    console.log('Current user onboardingStep:', user?.onboardingStep);
                                    setNavigating(true);
                                    try {
                                        // Refresh user data to get updated onboarding step
                                        console.log('Refreshing user data...');
                                        await refreshUser();
                                        console.log('User data refreshed');
                                        console.log('Navigating to step 5...');
                                        navigate('/onboarding/step-5');
                                    } catch (error) {
                                        console.error('Error navigating:', error);
                                        setNavigating(false);
                                    }
                                }}
                                disabled={navigating}
                                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {navigating ? 'Loading...' : 'Continue to Next Step'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Step 4 of 8</span>
                        <span className="text-sm font-medium text-emerald-600">Training Schedule</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: '50%' }}
                        ></div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Schedule Your Training
                        </h1>
                        <p className="text-gray-600">
                            Select your preferred time slots for training. We'll confirm the best time that works for both of us.
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Meeting Type Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Preferred Meeting Type
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, meetingType: 'phone' })}
                                    className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                                        formData.meetingType === 'phone'
                                            ? 'border-emerald-600 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Phone className="w-6 h-6 text-emerald-600" />
                                    <span className="font-medium">Phone Call</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, meetingType: 'zoom' })}
                                    className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                                        formData.meetingType === 'zoom'
                                            ? 'border-emerald-600 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Video className="w-6 h-6 text-emerald-600" />
                                    <span className="font-medium">Zoom Meeting</span>
                                </button>
                            </div>
                        </div>

                        {/* Phone Number (if phone selected) */}
                        {formData.meetingType === 'phone' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={handlePhoneChange}
                                    onBlur={() => {
                                        if (formData.phoneNumber) {
                                            setPhoneError(validatePhoneNumber(formData.phoneNumber));
                                        }
                                    }}
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                                        phoneError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                                    }`}
                                    placeholder="(555) 123-4567"
                                    aria-invalid={!!phoneError}
                                    aria-describedby={phoneError ? 'phone-error' : undefined}
                                />
                                {phoneError && (
                                    <p id="phone-error" className="mt-1 text-xs text-red-600">
                                        {phoneError}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Timezone */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Your Timezone
                            </label>
                            <input
                                type="text"
                                value={formData.timezone}
                                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50"
                                readOnly
                            />
                        </div>

                        {/* Time Slot Pickers */}
                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Select Your Preferred Times
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Please provide at least 1 and up to 3 preferred time slots. We'll do our best to accommodate your schedule.
                            </p>

                            <div className="space-y-4">
                                <TimeSlotPicker
                                    label="First Preference"
                                    value={formData.preferredSlot1}
                                    onChange={(date) => setFormData({ ...formData, preferredSlot1: date as any })}
                                    required
                                    minDate={new Date()}
                                />

                                <TimeSlotPicker
                                    label="Second Preference (Optional)"
                                    value={formData.preferredSlot2 || null}
                                    onChange={(date) => setFormData({ ...formData, preferredSlot2: date || undefined })}
                                    minDate={new Date()}
                                />

                                <TimeSlotPicker
                                    label="Third Preference (Optional)"
                                    value={formData.preferredSlot3 || null}
                                    onChange={(date) => setFormData({ ...formData, preferredSlot3: date || undefined })}
                                    minDate={new Date()}
                                />
                            </div>
                        </div>

                        {/* Additional Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Additional Notes (Optional)
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                placeholder="Any specific requirements or preferences..."
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleSkip}
                                className="text-gray-600 hover:text-gray-800 font-medium"
                            >
                                Skip for Now
                            </button>

                            <button
                                type="submit"
                                disabled={isLoading || !formData.preferredSlot1}
                                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? (
                                    <span className="flex items-center">
                                        <LoadingSpinner size="sm" />
                                        <span className="ml-2">Submitting...</span>
                                    </span>
                                ) : (
                                    'Submit Availability →'
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Help Text */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                        Need help? Contact your recruiter or{' '}
                        <a href="#" className="text-emerald-600 hover:text-emerald-700">
                            support@turtleconnector.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
